import '../Main.scss';

import * as React from 'react';
import { connect } from 'react-redux';

import { ApplicationState } from '../../Store';
import { GetDefaultSubmissionOptions } from '../../Store/AppSettings';
import {
    actionCreators as ImportFinancialsActionCreators,
    GetBorrowerParametersActive,
} from '../../Store/ImportFinancials';
import {
    GetRequestedOptions,
    GetRequestedOptionsId,
} from '../../Store/NewDataCollectionParameters';
import {
    BrandConfig,
    GetBrandConfig,
    GetTenantName,
} from '../../Store/Tenant';
import { GetPortalText } from '../../Store/PortalAssets';

import { AccountingPkgPresentation } from '../SelectAccountingSystem/AccountingPkgPresentation';
import { BorrowerParametersModal } from './BorrowerParametersModal';
import { PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';
import { AccountingCollectionGroup } from '../../Models/AccountingCollectionGroup';

import { AttributedParagraphSet } from '../../Utils/AttributedParagraph';
import { knownAccountingGroupIds } from '../../Models/AccountingCollectionGroup';
import { StringFormat } from '../../Utils/StringUtils';

import { 
    PortalTextCommon,
    PortalTextBorrowerParameters,
    PortalTextCommonImport,
} from '../../Models/Api/strongbox.financialportal';

type InjectedReduxState = {
    brandConfig: BrandConfig;
    tenantVisibleName: string;
    active: boolean;
    parameters: PortalCreateSubmissionRequestOptions | undefined;
    parametersId: number | undefined;
    reportsList: AttributedParagraphSet;
    securityBullets: AttributedParagraphSet;
    commonText?: PortalTextCommon;
    borrowerParametersText?: PortalTextBorrowerParameters;
};

type InjectedActionCreators = typeof ImportFinancialsActionCreators;

type BorrowerParametersContainerProps = {
    accountingPkg?: AccountingPkgPresentation;
    dismissModal: (cancelled: boolean) => void;
};

type Props = BorrowerParametersContainerProps  & InjectedActionCreators & InjectedReduxState;

export const pgIconDescriptors = { 
    secure: 'secure',
    private: 'private',
    readonly: 'read-only',
}

const BorrowerParametersContainerComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        parameters,
        reportsList,
        borrowerParametersText,
    } = props;

    const reportListParagraphs = React.useMemo(() => {
        const result: string[] = [];

        if (!(reportsList && reportsList.paragraphs)) {
            return result;
        }

        reportsList.paragraphs.forEach(pg => {
            let content: string | undefined = pg.content;

            const requiresAttr = pg.attributes.find(attr => attr.name === 'requires-group');

            if (!!requiresAttr) {
                const ag = parameters?.accountingCollectionGroups.get(requiresAttr.value);
                if (!(ag && ag.collect)) {
                    content = undefined;
                }
            }
            if (!!content) {
                result.push(content);
            }
        });

        return result;
    }, [parameters, reportsList])

    if (!(props.active && parameters)) {
        return (<></>);
    }

    return (
        <BorrowerParametersModal
            lenderName={props.tenantVisibleName}
            dismissModal={props.dismissModal}
            parameters={parameters}
            brandConfig={props.brandConfig}
            reportsList={reportListParagraphs}
            accountingPkg={props.accountingPkg}
            securityBullets={props.securityBullets}
            borrowerParametersText={borrowerParametersText}
        />
    );
}

export const BorrowerParametersContainer = connect<InjectedReduxState, InjectedActionCreators, BorrowerParametersContainerProps, ApplicationState>(
    (appState: ApplicationState) => {
        const reportsListContent = GetPortalText<PortalTextBorrowerParameters>(appState, 'borrowerParameters');
        const commonImportText = GetPortalText<PortalTextCommonImport>(appState,'commonImport');
        const tenantVisibleName = GetTenantName(appState);
        
        const result: InjectedReduxState = {
            active: GetBorrowerParametersActive(appState),
            tenantVisibleName,
            parameters: GetRequestedOptions(appState),
            parametersId: GetRequestedOptionsId(appState),
            brandConfig: GetBrandConfig(appState),
            reportsList: {
                paragraphs: [
                    {
                        attributes: [{ name: 'requires-group', value: knownAccountingGroupIds.financialStatements }],
                        content: commonImportText?.financialStatements || '',
                    },
                    {
                        attributes: [{ name: 'requires-group', value: knownAccountingGroupIds.accountsReceivable }],
                        content: commonImportText?.arTransactions || '',
                    },
                    {
                        attributes: [{ name: 'requires-group', value: knownAccountingGroupIds.accountsPayable }],
                        content: commonImportText?.apTransactions || '',
                    },
                    {
                        attributes: [],
                        content: commonImportText?.generalLedger || '',
                    },
                    {
                        attributes: [],
                        content: commonImportText?.coa || '',
                    }
                ]
            },
            securityBullets: {
                paragraphs: [
                    {
                        attributes: [
                            { name: 'bullet', value: 'true' },
                            { name: 'iconDescriptor', value: pgIconDescriptors.secure }
                        ],
                        content: reportsListContent?.securityBulletSecure || '',
                    },
                    {
                        attributes: [],
                        content: reportsListContent?.securityBulletEncrypted || '',
                    },
                    {
                        attributes: [
                            { name: 'bullet', value: 'true' },
                            { name: 'iconDescriptor', value: pgIconDescriptors.private }
                        ],
                        content: reportsListContent?.securityBulletPrivate || '',
                    },
                    {
                        attributes: [{ name: 'replaceLenderName', value: 'true' }],
                        content: StringFormat(reportsListContent?.templateSecurityBulletSecureCredentials || '{0}', tenantVisibleName),
                    },
                    {
                        attributes: [{ name: 'anonymizedOnly', value: 'true' }],
                        content: reportsListContent?.securityBulletCustomerAndVendorsSecure || '',
                    },
                    {
                        attributes: [
                            { name: 'bullet', value: 'true' },
                            { name: 'iconDescriptor', value: pgIconDescriptors.readonly }
                        ],
                        content: reportsListContent?.securityBulletReadOnlyConnection || '',
                    },
                    {
                        attributes: [],
                        content: reportsListContent?.securityBulletInformationNotAltered || '',
                    },
                    {
                        attributes: [],
                        content: reportsListContent?.securityBulletOneWayData || '',
                    },
                ]
            },
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            borrowerParametersText: GetPortalText<PortalTextBorrowerParameters>(appState, 'borrowerParameters'),
        };

        if (!result.parameters) {
            const defaultAnonymous = GetDefaultSubmissionOptions(appState, 'shareableanonymous', true, commonImportText);

            result.parameters = {
                basisOfAccountingPreference: defaultAnonymous.basisOfAccountingPreference,
                mostRecentPeriodMonth: defaultAnonymous.mostRecentPeriodMonth,
                mostRecentPeriodYear: defaultAnonymous.mostRecentPeriodYear,
                mostRecentPeriodDay: defaultAnonymous.mostRecentPeriodDay,
                fullFiscalYearsToCollect: defaultAnonymous.fullFiscalYearsToCollect,
                anonymizeCustomersAndVendors: defaultAnonymous.anonymizeCustomersAndVendors,
                provideUserCopy: defaultAnonymous.provideUserCopy,
                allowUserUpload: defaultAnonymous.allowUserUpload,
                hasShareableLink: false,
                accountingCollectionGroups: new Map<string, AccountingCollectionGroup>(defaultAnonymous.accountingCollectionGroups),
                endOfImport: defaultAnonymous.endOfImport,
            }
        }

        return result;
    },
    {
        ...ImportFinancialsActionCreators
    }
)(BorrowerParametersContainerComponent);
